import { type FontStyleValue, type RichTextSpan } from '@meitu/x-design-text';
import { colorToRgbaUint32 } from '@/utils/color-kit';
import { type ILayerProps, type ITextContent, type ITextLayerProps } from '@/version-types';
import { resolveFontFamily } from './font';

/**
 * 将图层文本样式转换为文本引擎 span 属性。
 *
 * @param layer - ILayerProps，当前文本图层。
 * @param styleSource - object，图层级或分段级文本样式来源。
 * @returns RichTextSpan['attrs']，文本引擎可消费的分段样式。
 */
const createSpanAttrs = (layer: ILayerProps, item: ITextContent): RichTextSpan['attrs'] => {
    const data = layer.data as ITextLayerProps;
    const style: FontStyleValue = item.italic ? 'italic' : 'normal';
    const subset = resolveFontFamily(layer, item.fontFamily);

    const attrs: RichTextSpan['attrs'] = {
        family: subset,
        weight: item.bold ? 700 : 400,
        style,
        underline: item.underline ? 'single' : undefined,
        underlinePosition: 'auto',
        decorationThickness: 'auto',
        strikethrough: item.lineThrough || undefined,
        colorRgba: colorToRgbaUint32(item.color),
    };

    if (item.fontSize !== data.fontSize) {
        attrs.fontSize = item.fontSize;
    }

    return attrs;
};

/**
 * 将 render 富文本分段转换为文本引擎 RichTextSpan。
 *
 * @param layer -文本图层。
 * @returns 文本引擎可消费的富文本分段。
 */
export const createRichTextSpans = (layer: ILayerProps): RichTextSpan[] => {
    const data = layer.data as ITextLayerProps;
    const { textContents = [] } = data;

    if (textContents.length === 0) {
        return [
            {
                text: data.text,
                attrs: createSpanAttrs(layer, data),
            },
        ];
    }

    return textContents.map((item) => {
        return {
            text: item.text,
            attrs: createSpanAttrs(layer, item),
        };
    });
};

/**
 * 统一 textContent 中的属性
 *
 * @param textContents - 需要统一属性的 textContent 集合。
 * @param attrs - 要统一设置的属性。
 * @returns 统一后的文本内容集合。
 */
export const unifyTextContentsAttrs = (textContents: ITextContent[], attrs: Partial<ITextContent>): ITextContent[] => {
    return textContents.map((item) => ({ ...item, ...attrs }));
};
